import { treatmentPages } from "../routes/Services/serviceData";

export const BOOKING_URL =
  "https://bookings.gettimely.com/rnradianceaesthetics/bb/book";

// Google Ads "Book appointment" conversion action, measured on click (see
// Tools & Settings > Conversions). Value is a placeholder average booking
// value, not a real transaction amount — booking confirmation happens on
// Timely's domain, so this fires at the moment of intent (the click), not
// on completion.
const GOOGLE_ADS_CONVERSION_ID = "AW-18356734203/eM2DCLiSq9gcEPuRlrFE";

// Maps a pathname to the page_name/page_type params GA4 events use. Kept
// here (not per-component) so every event that references "which page was
// this on" agrees with what the page_view event itself reports.
const STATIC_PAGE_META = {
  "/": { page_name: "Home", page_type: "homepage" },
  "/treatments": { page_name: "All Treatments", page_type: "service_listing" },
  "/packages": { page_name: "Packages", page_type: "packages" },
  "/reviews": { page_name: "Reviews", page_type: "reviews" },
  "/gallery": { page_name: "Gallery", page_type: "gallery" },
  "/medik8": { page_name: "Medik8", page_type: "consultation" },
  "/aftercare": { page_name: "Aftercare", page_type: "info" },
  "/cancellation-policy": { page_name: "Cancellation Policy", page_type: "legal" },
  "/terms-conditions": { page_name: "Terms & Conditions", page_type: "legal" },
  "/privacy-policy": { page_name: "Privacy Policy", page_type: "legal" },
};

// Built from serviceData.treatmentPages so /treatments/:slug pages report
// their category title instead of a generic name.
const treatmentPageMeta = treatmentPages.reduce((acc, { slug, title }) => {
  acc[slug] = { page_name: title, page_type: "service_category" };
  return acc;
}, {});

// Central page_name/page_type resolver used by trackPageView and by
// components that need to tag an event with "which page is this".
export const getPageMeta = (pathname) => {
  if (STATIC_PAGE_META[pathname]) return STATIC_PAGE_META[pathname];

  const categoryMatch = pathname.match(/^\/treatments\/([^/]+)\/?$/);
  if (categoryMatch) {
    return (
      treatmentPageMeta[categoryMatch[1]] || {
        page_name: "Treatment Category",
        page_type: "service_category",
      }
    );
  }

  return { page_name: pathname, page_type: "other" };
};

// Every tracking helper below funnels through this so the gtag guard only
// lives in one place.
const sendEvent = (name, params = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", name, params);
};

export const trackEvent = sendEvent;

// GA4's gtag config only auto-fires page_view once, on hard load — React
// Router navigations never trigger another one. Called from the route
// listener in routes.js on every pathname change.
export const trackPageView = (pathname) => {
  const { page_name, page_type } = getPageMeta(pathname);
  sendEvent("page_view", {
    page_path: pathname,
    page_location: typeof window !== "undefined" ? window.location.href : pathname,
    page_title: typeof document !== "undefined" ? document.title : undefined,
    page_name,
    page_type,
  });
};

// Fires on every "Book Now" click so Google Ads (once linked to GA4) can see
// which page/treatment actually drove a booking click. This works with the
// gtag.js snippet already in public/index.html — it does not require Google
// Tag Manager.
export const trackBookingClick = (source) => {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const { page_type } = getPageMeta(pathname);

  sendEvent("book_now_click", {
    event_category: "engagement",
    booking_source: source,
    page_path: pathname,
    page_type,
  });

  sendEvent("conversion", {
    send_to: GOOGLE_ADS_CONVERSION_ID,
    value: 1.0,
    currency: "GBP",
  });
};

// tel:/mailto:/directions links in the Navbar, Footer and homepage.
export const trackContactClick = (method, source) => {
  sendEvent("contact_click", {
    event_category: "engagement",
    contact_method: method,
    button_location: source,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });
};

// Instagram/Facebook/Google Business Profile icon clicks.
export const trackSocialClick = (network, source) => {
  sendEvent("social_click", {
    event_category: "engagement",
    social_network: network,
    button_location: source,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  });
};

// Navbar/Footer navigation link clicks, treatments-dropdown open, mobile
// drawer open.
export const trackNavClick = (label, source) => {
  sendEvent("nav_click", {
    event_category: "navigation",
    link_text: label,
    button_location: source,
  });
};

// Homepage FAQ accordion.
export const trackFaqToggle = (question, index, opening) => {
  if (!opening) return; // only meaningful signal is "this question was opened"
  sendEvent("faq_open", {
    event_category: "engagement",
    faq_question: question,
    faq_index: index,
  });
};

// Treatment accordion expand in ServiceAccordion.jsx — captures interest in
// a specific treatment independent of whether the visitor then books.
export const trackAccordionExpand = (serviceName, source) => {
  sendEvent("treatment_view", {
    event_category: "engagement",
    treatment_name: serviceName,
    button_location: source,
  });
};

// Reviews carousel (both the homepage mini-carousel and the full /reviews
// page carousel).
export const trackCarouselInteraction = (component, action, index) => {
  sendEvent("carousel_interaction", {
    event_category: "engagement",
    carousel_name: component,
    interaction_type: action,
    item_index: index,
  });
};

// Homepage treatment-overview cards linking into a category page.
export const trackTreatmentCardClick = (treatmentName, destination, source) => {
  sendEvent("select_content", {
    event_category: "engagement",
    content_type: "treatment_card",
    treatment_name: treatmentName,
    button_location: source,
    destination,
  });
};

// Gallery lightbox open.
export const trackGalleryImageView = (index, alt) => {
  sendEvent("gallery_image_view", {
    event_category: "engagement",
    image_index: index,
    image_alt: alt,
  });
};

// Scroll depth milestones (see useScrollDepth.js) and section-visibility
// signals (see useSectionView.js).
export const trackScrollDepth = (percent, source) => {
  sendEvent("scroll_depth", {
    event_category: "engagement",
    scroll_percentage: percent,
    page_path: source,
  });
};

export const trackSectionView = (sectionName) => {
  sendEvent("section_view", {
    event_category: "engagement",
    section_name: sectionName,
  });
};

// Booking widget (Phase 6) — best-effort signals only. Timely has no public
// JS API/postMessage events for its booking iframe, so "focus" is a
// window-blur heuristic approximation, not a true in-widget interaction.
export const trackWidgetEvent = (action, source) => {
  sendEvent(`booking_widget_${action}`, {
    event_category: "engagement",
    button_location: source,
  });
};
